import React from 'react';
import { Card, Row, Col, Select, DatePicker, Input, Button, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DataTable, PageLayout } from '../../ui';
import dayjs from 'dayjs';
import { useGetAuditLogsQuery, AuditLogItem } from '../../store/api/auditApi';
import { useGetUsersQuery } from '../../store/api/usersApi';
import { formatUserDisplayName } from '../../utils/userDisplayName';

const { RangePicker } = DatePicker;
const { Option } = Select;

const AuditPage: React.FC = () => {
  const [filters, setFilters] = React.useState({
    page: 1,
    limit: 20,
    userId: undefined as number | undefined,
    action: '',
    entityType: '',
    entityId: '' as string | number,
    dateRange: null as [dayjs.Dayjs, dayjs.Dayjs] | null,
    search: ''
  });

  const { data: usersResp } = useGetUsersQuery();
  const users = usersResp || [];

  const { data, isFetching, refetch } = useGetAuditLogsQuery({
    page: filters.page,
    limit: filters.limit,
    userId: filters.userId,
    action: filters.action || undefined,
    entityType: filters.entityType || undefined,
    entityId: typeof filters.entityId === 'number' ? filters.entityId : filters.entityId ? Number(filters.entityId) : undefined,
    dateFrom: filters.dateRange?.[0]?.format('YYYY-MM-DD'),
    dateTo: filters.dateRange?.[1]?.format('YYYY-MM-DD'),
    search: filters.search || undefined
  });

  const onReset = () => {
    setFilters({
      page: 1,
      limit: 20,
      userId: undefined,
      action: '',
      entityType: '',
      entityId: '',
      dateRange: null,
      search: ''
    });
  };

  const columns: ColumnsType<AuditLogItem> = [
    {
      title: 'Дата',
      dataIndex: 'createdAt',
      width: 170,
      render: (v: string, record) => (
        <div>
          <div>{dayjs(v).format('DD.MM.YYYY HH:mm')}</div>
          {record.eventDate && <div style={{ color: 'var(--text-secondary)' }}>событие: {dayjs(record.eventDate).format('DD.MM.YYYY HH:mm')}</div>}
        </div>
      )
    },
    {
      title: 'Пользователь',
      dataIndex: 'userName',
      width: 180,
      render: (_: any, r) => {
        const liveName = r.user ? formatUserDisplayName(r.user) : '—';
        return liveName !== '—' ? liveName : (r.userName || 'Система');
      }
    },
    {
      title: 'Действие',
      dataIndex: 'action',
      width: 180,
      render: (a: string) => <Tag>{a}</Tag>
    },
    {
      title: 'Сущность',
      key: 'entity',
      width: 180,
      render: (r: AuditLogItem) => (
        <div>
          <div>{r.entityType || '—'}</div>
          <div style={{ color: 'var(--text-secondary)' }}>ID: {r.entityId ?? '—'}</div>
        </div>
      )
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      ellipsis: true
    },
    {
      title: 'IP',
      dataIndex: 'ipAddress',
      width: 140
    }
  ];

  return (
    <PageLayout title="Журнал аудита" subtitle="Действия пользователей в системе">
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={6}>
            <Select
              allowClear
              showSearch
              optionFilterProp="children"
              placeholder="Пользователь"
              value={filters.userId}
              onChange={(val) => setFilters((f) => ({ ...f, userId: val, page: 1 }))}
              style={{ width: '100%' }}
            >
              {users.map((u: any) => (
                <Option key={u.id} value={u.id}>
                  {formatUserDisplayName(u)}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Действие"
              value={filters.action}
              onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value, page: 1 }))}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="Тип сущности (например, Route, Cargo)"
              value={filters.entityType}
              onChange={(e) => setFilters((f) => ({ ...f, entityType: e.target.value, page: 1 }))}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input
              placeholder="ID сущности"
              value={filters.entityId}
              onChange={(e) => setFilters((f) => ({ ...f, entityId: e.target.value, page: 1 }))}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.dateRange as any}
              onChange={(v) => setFilters((f) => ({ ...f, dateRange: v as any, page: 1 }))}
              showTime
              format="DD.MM.YYYY HH:mm"
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Поиск (описание, действие, пользователь, IP)"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value, page: 1 }))}
            />
          </Col>
          <Col xs={24} sm={12} md={8} style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={onReset}>Сбросить</Button>
            <Button type="primary" onClick={() => refetch()}>Обновить</Button>
          </Col>
        </Row>
      </Card>

      <Card>
        <DataTable
          rowKey="id"
          columns={columns}
          loading={isFetching}
          dataSource={data?.items || []}
          pagination={{
            current: filters.page,
            pageSize: filters.limit,
            total: data?.total || 0,
            onChange: (page, pageSize) => setFilters((f) => ({ ...f, page, limit: pageSize }))
          }}
        />
      </Card>
    </PageLayout>
  );
};

export default AuditPage;


